import React, { useContext, useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useNavigate } from "react-router";
import { LuPalette } from "react-icons/lu";
import { Images } from "../controller/Images";
import { GlobalStateContext } from "../controller/GlobalState";
import { emailRegex, isValidPhone, companyServices, aiServicePages, tech, localSeoFor, relatedServiceLinks } from "../controller/global";
import { submitLead } from "../controller/leads";

const service = companyServices.find((s) => s.key === "ui_ux");
const otherServices = companyServices.filter((s) => s.key !== "ui_ux");

// Same trust-logo set shown across the rest of the site.
const TRUST_LOGOS = [
   // { name: "Aptor", src: Images.aptor_logo },
  { name: "BoxERP", src: Images.boxErp },
  { name: "GE", src: Images.ge_logo },
  { name: "Jennison", src: Images.Jennison_Dark_logo },
  { name: "Regent", src: Images.regent_logo },
  { name: "ThreeLockBox", src: Images.threelockbox_logo },
  { name: "Stokkd", src: Images.stokkd_logo },
  { name: "Ujala Enterprises", src: Images.UjalaEnterprises_logo },
];

// Real facets already defined for this service in controller/global.js
// (SERVICE_FACETS.ui_ux), given their own feature rows here instead of just
// an anchor link.
const FACETS = [
  {
    title: "Wireframing & Prototyping",
    path: "/wireframing-prototyping",
    blurb: service.description[2],
    tags: ["Low-fidelity wireframes", "Interactive prototypes", "User testing"],
  },
  {
    title: "User Research",
    path: "/user-research",
    blurb: service.description[4],
    tags: ["User interviews", "Usability testing", "Persona mapping"],
  },
];

// Honest before/after — general states, not a specific client's fabricated
// metrics, grounded in the same real description bullets shown above.
const BEFORE_AFTER = {
  before: [
    "Users abandon flows they can't figure out",
    "Every screen looks and behaves a little differently",
    "Design decisions are guesses, not tested choices",
    "Engineering builds first, fixes usability later",
  ],
  after: [
    "Flows are wireframed and tested before a single screen is built",
    "A consistent design system ties every screen together",
    "Every major decision is grounded in real user research",
    "Developer-ready specs mean design and engineering move together",
  ],
};

const PROCESS_STEPS = [
  { n: "01", t: "Discover", d: "User research and stakeholder interviews to understand real needs, not assumptions." },
  { n: "02", t: "Wireframe", d: "Low-fidelity structure and user flows, validated before any visual design begins." },
  { n: "03", t: "Prototype & Test", d: "High-fidelity, interactive prototypes tested with real users before a line of code is written." },
  { n: "04", t: "Handoff", d: "Developer-ready design specs and a design system your engineering team can actually build from." },
];

// Real tools from the same design category shown on /technologies.
const designTools = tech.find((t) => t.key === "design")?.children.map((c) => c.name) ?? [];
const TOOL_ROWS = [
  { a: designTools[0] ?? "Figma", b: "PROTOTYPING" },
  { a: designTools[1] ?? "Adobe XD", b: "WIREFRAMING" },
  { a: designTools[2] ?? "Photoshop", b: "VISUAL DESIGN" },
];

const FACTS = [
  { a: "MSME", b: "Registered IT company" },
  { a: "Delhi NCR", b: "Headquarters" },
  { a: "7", b: "Stage delivery process" },
  { a: "11", b: "Core services offered" },
];

const ENGAGEMENT_RESULTS = [
  { a: "Fixed-Scope", b: "Clearly defined deliverable" },
  { a: "Dedicated Team", b: "Ongoing, evolving product work" },
  { a: "MVP Sprint", b: "Validate an idea fast" },
  { a: "1", b: "Same process, every model" },
];

const FAQS = [
  {
    q: "What does your UI/UX design process include?",
    a: "Wireframing, prototyping, and user testing — we prioritize user research to understand your audience, then design interfaces that are engaging, functional, and reflect your brand identity.",
  },
  {
    q: "Can we just get research, or just design — not both?",
    a: "Yes. Wireframing & Prototyping and User Research are separate, real parts of this service, so you can bring us in for a focused need or for the full end-to-end process.",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] } }),
};

// Framer Motion writes its own `transform`, which silently overrides any CSS
// `transform: rotate()` on the same element — so the engine panel's tilt has
// to be animated in, not declared in CSS.
const fadeUpRotate = {
  hidden: { opacity: 0, y: 18, rotate: 0 },
  show: (i = 0) => ({ opacity: 1, y: 0, rotate: 1.5, transition: { duration: 0.6, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] } }),
};

function Reveal({ as = "div", i = 0, className, children, reduceMotion, ...rest }) {
  const MotionTag = motion[as] || motion.div;
  return (
    <MotionTag
      className={className}
      custom={i}
      variants={fadeUp}
      initial={reduceMotion ? false : "hidden"}
      whileInView="show"
      viewport={{ once: true, amount: 0.2 }}
      {...rest}
    >
      {children}
    </MotionTag>
  );
}

// UI/UX Design gets its own bespoke page (like the AI services and Custom
// Software Development) instead of sharing the generic ServiceDetail
// template used by the other ten non-AI services — same section-for-section
// structure as /generative-ai-development and /services, with real content
// pulled from this service's existing description and facets.
const UIUXDesign = () => {
  const navigate = useNavigate();
  const { setIsOpen, formData, setFormData } = useContext(GlobalStateContext);
  const [openFaq, setOpenFaq] = useState(-1);
  const [contactErrors, setContactErrors] = useState({});
  const [contactStatus, setContactStatus] = useState("idle");
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    const seo = localSeoFor(
      "/ui-ux-design",
      `${service.title} | InQmise Technologies`,
      service.description[0].slice(0, 160)
    );
    document.title = seo.title;

    const description = document.createElement("meta");
    description.name = "description";
    description.content = seo.description;
    document.head.appendChild(description);

    let canonical = document.querySelector("link[rel='canonical']");
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", `https://inqmisetechnologies.com${service.path}`);

    const schemaScript = document.createElement("script");
    schemaScript.type = "application/ld+json";
    schemaScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      "name": service.title,
      "description": service.description[0],
      "url": `https://inqmisetechnologies.com${service.path}`,
      "provider": {
        "@id": "https://inqmisetechnologies.com/#organization",
        "@type": "Organization",
        "name": "InQmise Technologies",
        "url": "https://inqmisetechnologies.com",
        "telephone": "+918130409466",
      },
      "areaServed": ["IN", "US", "GB", "CA", "AU", "AE", "SG"],
      "serviceType": service.title,
    });
    document.head.appendChild(schemaScript);

    const breadcrumbScript = document.createElement("script");
    breadcrumbScript.type = "application/ld+json";
    breadcrumbScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://inqmisetechnologies.com" },
        { "@type": "ListItem", "position": 2, "name": "Services", "item": "https://inqmisetechnologies.com/services" },
        { "@type": "ListItem", "position": 3, "name": service.title, "item": `https://inqmisetechnologies.com${service.path}` },
      ],
    });
    document.head.appendChild(breadcrumbScript);

    const faqScript = document.createElement("script");
    faqScript.type = "application/ld+json";
    faqScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": FAQS.map((item) => ({
        "@type": "Question",
        "name": item.q,
        "acceptedAnswer": { "@type": "Answer", "text": item.a },
      })),
    });
    document.head.appendChild(faqScript);
  }, []);

  const validateContactField = (name, value) => {
    switch (name) {
      case "fullname":
        return value.trim() ? "" : "Full name is required.";
      case "phone":
        if (!value.trim()) return "";
        return isValidPhone(value) ? "" : "Enter a valid phone number.";
      case "email":
        if (!value.trim()) return "Email is required.";
        return emailRegex.test(value.toLowerCase()) ? "" : "Enter a valid email address.";
      case "subject":
        return value.trim() ? "" : "Subject is required.";
      case "description":
        return value.trim() ? "" : "Tell us a little about your project.";
      default:
        return "";
    }
  };

  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setContactErrors((prev) => ({ ...prev, [name]: validateContactField(name, value) }));
  };

  const handleContactSubmit = (e) => {
    e.preventDefault();
    const newErrors = {
      fullname: validateContactField("fullname", formData.fullname),
      phone: validateContactField("phone", formData.phone),
      email: validateContactField("email", formData.email),
      subject: validateContactField("subject", formData.subject),
      description: validateContactField("description", formData.description),
    };
    if (Object.values(newErrors).some(Boolean)) {
      setContactErrors(newErrors);
      return;
    }

    setContactStatus("submitting");
    submitLead(formData, "page")
      .then(() => {
        setContactStatus("success");
        setFormData({ fullname: "", phone: "", email: "", subject: "", description: "" });
        setContactErrors({});
      })
      .catch(() => {
        setContactStatus("error");
      });
  };

  const openContactModal = () => setIsOpen(true);

  return (
    <main className="editorial-ai pt-20">
      {/* HERO */}
      <header className="ea-hero ea-wrap">
        <Reveal as="div" reduceMotion={reduceMotion}>
          <p className="ea-eyebrow">UI/UX Design · built to convert</p>
          <h1>
            UI/UX design people <em>actually enjoy</em> using.
          </h1>
          <p className="ea-lede">{service.description[1]}</p>
          <div className="ea-actions">
            <button className="ea-button ea-primary" onClick={openContactModal} aria-label="Open contact form to start your design project">
              Start your project ↗
            </button>
            <a className="ea-text-link" href="#process">See our process</a>
          </div>
        </Reveal>

        <Reveal as="aside" i={1} reduceMotion={reduceMotion} variants={fadeUpRotate} className="ea-engine">
          <div className="ea-engine-top"><span /> INQMISE / UI/UX DESIGN <b>LIVE</b></div>
          <h2><LuPalette aria-hidden="true" style={{ verticalAlign: "-4px", marginRight: 8 }} />{service.description[3]}</h2>
          <div className="ea-query"><small>PROCESS</small>{service.description[2]}</div>
          <div className="ea-query"><small>RESEARCH</small>{service.description[4]}</div>
          <div className="ea-flow"><b>Research</b><i>→</i><b>Design</b><i>→</i><b>Test</b></div>
        </Reveal>
      </header>

      {/* TRUST LOGOS */}
      <div className="ea-wrap ea-logo-row">
        <span>TRUSTED BY GROWING BUSINESSES</span>
        <div className="ea-logo-track">
          <div className="ea-logo-track-inner">
            {[...TRUST_LOGOS, ...TRUST_LOGOS].map((logo, i) => (
              <img key={`${logo.name}-${i}`} src={logo.src} alt={logo.name} loading="lazy" />
            ))}
          </div>
        </div>
      </div>

      {/* WHAT CHANGES — before/after comparison */}
      <section className="ea-wrap ea-section">
        <Reveal as="div" className="ea-section-head">
          <div>
            <div className="ea-eyebrow">What changes</div>
            <h2>Same product,<br />a very different experience.</h2>
          </div>
          <p>Professional UX design changes how a product feels to use, not just how it looks.</p>
        </Reveal>
        <div className="ai-diff">
          <Reveal as="div" reduceMotion={reduceMotion} className="ai-diff-col">
            <h3>Without professional UX design</h3>
            <ul>
              {BEFORE_AFTER.before.map((item) => (
                <li key={item}><span className="ai-diff-mark">✕</span><span>{item}</span></li>
              ))}
            </ul>
          </Reveal>
          <Reveal as="div" i={1} reduceMotion={reduceMotion} className="ai-diff-col ai-diff-after">
            <h3>With InQmise design</h3>
            <ul>
              {BEFORE_AFTER.after.map((item) => (
                <li key={item}><span className="ai-diff-mark">✓</span><span>{item}</span></li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* OUR PROCESS — alternating vertical timeline */}
      <section className="ea-services" id="process">
        <div className="ea-wrap ea-section">
          <Reveal as="div" className="ea-section-head">
            <div>
              <div className="ea-eyebrow">Our process</div>
              <h2>Four stages,<br />zero guesswork.</h2>
            </div>
            <p>Every stage exists to make the next decision safer.</p>
          </Reveal>
          <div className="aa-process-line">
            {PROCESS_STEPS.map((step, i) => (
              <Reveal as="div" i={i + 1} reduceMotion={reduceMotion} className="aa-process-row" key={step.n}>
                <div className="aa-process-copy">
                  <h3>{step.t}</h3>
                  <p>{step.d}</p>
                </div>
                <div className="aa-process-node">{step.n}</div>
                <div />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TWO WAYS TO ENGAGE — the real facets, as full feature rows */}
      <section className="ea-wrap ea-section">
        <Reveal as="div" className="ea-section-head">
          <div>
            <div className="ea-eyebrow">How to engage</div>
            <h2>Two real ways<br />to work with us.</h2>
          </div>
          <p>Each is a complete, real part of this service — not a marketing category.</p>
        </Reveal>

        {FACETS.map((f, i) => (
          <Reveal as="article" i={i + 1} reduceMotion={reduceMotion} className="ea-feature" key={f.path}>
            <div className="ea-num">{String(i + 1).padStart(2, "0")}</div>
            <h3>{f.title}</h3>
            <p>{f.blurb}</p>
            <div className="ea-tags">
              {f.tags.map((tag) => <span key={tag}>{tag}</span>)}
            </div>
            <a
              className="ea-text-link"
              href={f.path}
              style={{ gridColumn: "2 / 4", marginTop: 4 }}
              onClick={(e) => { e.preventDefault(); navigate(f.path); }}
            >
              Explore {f.title} →
            </a>
          </Reveal>
        ))}
      </section>

      {/* WHAT WE DESIGN WITH + FACTS */}
      <section className="ea-models">
        <div className="ea-wrap ea-section">
          <Reveal as="div" className="ea-section-head">
            <div>
              <div className="ea-eyebrow">What we design with</div>
              <h2>Real tools,<br />real numbers.</h2>
            </div>
            <p>The software behind every deliverable, and the facts behind the team.</p>
          </Reveal>
          <div className="ea-model-layout">
            <Reveal as="div" className="ea-model-panel">
              <h3>Our design toolkit</h3>
              {TOOL_ROWS.map((row) => (
                <div key={row.a}><span>{row.a}</span><small>{row.b}</small></div>
              ))}
            </Reveal>
            <div className="ea-metrics">
              {FACTS.map((m, i) => (
                <Reveal as="div" i={i + 1} reduceMotion={reduceMotion} className="ea-metric" key={m.a}>
                  <strong>{m.a}</strong>
                  <span>{m.b}</span>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ENGAGEMENT MODELS — case-study-shaped spotlight */}
      <section className="ea-wrap ea-section">
        <Reveal as="div" className="ea-section-head">
          <div>
            <div className="ea-eyebrow">Engagement models</div>
            <h2>Three ways to work<br />with us.</h2>
          </div>
          <p>One accountable team, whichever model fits your project.</p>
        </Reveal>
        <Reveal as="div" className="ea-case">
          <div>
            <div className="ea-eyebrow">FIXED-SCOPE / DEDICATED / MVP</div>
            <h3>Same process, one accountable team.</h3>
            <p>Whether you need a clearly scoped deliverable, an ongoing dedicated designer, or a fast MVP to validate an idea, the same process and the same team stay accountable from kickoff to handoff.</p>
            <button className="ea-text-link" onClick={openContactModal}>Discuss your project</button>
          </div>
          <div className="ea-results">
            {ENGAGEMENT_RESULTS.map((r) => (
              <div key={r.a}><b>{r.a}</b><span>{r.b}</span></div>
            ))}
          </div>
        </Reveal>
      </section>

      {/* RELATED SERVICES + AI SOLUTIONS cross-links */}
      <section className="ea-services">
        <div className="ea-wrap ea-section">
          <Reveal as="div" className="ea-section-head">
            <div>
              <div className="ea-eyebrow">Explore more</div>
              <h2>Other services<br />we offer.</h2>
            </div>
            <p>Every service follows the same disciplined process.</p>
          </Reveal>
          <div className="cs-tags-row" style={{ marginBottom: 32 }}>
            {(relatedServiceLinks["/ui-ux-design"] || []).map((s) => (<a key={s.path} className="cs-tag" href={s.path} style={{ cursor: "pointer", textDecoration: "none" }} onClick={(e) => { e.preventDefault(); navigate(s.path); }}>{s.title}</a>))}
            {otherServices.map((s) => (
              <a
                key={s.path}
                className="cs-tag"
                href={s.path}
                style={{ cursor: "pointer", textDecoration: "none" }}
                onClick={(e) => { e.preventDefault(); navigate(s.path); }}
              >
                {s.title}
              </a>
            ))}
            <a
              className="cs-tag"
              href="/services"
              style={{ cursor: "pointer", textDecoration: "none", fontWeight: 700 }}
              onClick={(e) => { e.preventDefault(); navigate("/services"); }}
            >
              All Services →
            </a>
          </div>

          <Reveal as="div" reduceMotion={reduceMotion} className="ea-section-head">
            <div>
              <div className="ea-eyebrow">Want to add AI?</div>
              <h2>Pair this with<br />an AI solution.</h2>
            </div>
            <p>Every design engagement can plug into our AI solutions when you're ready.</p>
          </Reveal>
          <div className="cs-tags-row">
            {aiServicePages.map((s) => (
              <a
                key={s.path}
                className="cs-tag"
                href={s.path}
                style={{ cursor: "pointer", textDecoration: "none" }}
                onClick={(e) => { e.preventDefault(); navigate(s.path); }}
              >
                {s.title}
              </a>
            ))}
            <a
              className="cs-tag"
              href="/ai-solutions"
              style={{ cursor: "pointer", textDecoration: "none", fontWeight: 700 }}
              onClick={(e) => { e.preventDefault(); navigate("/ai-solutions"); }}
            >
              All AI Solutions →
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="ea-faq-section">
        <div className="ea-wrap ea-faq ea-section">
          <Reveal as="div" className="ea-section-head">
            <div className="ea-eyebrow">Common questions</div>
            <h2>UI/UX design FAQs</h2>
            <p>Short answers to the questions we hear most.</p>
          </Reveal>
          {FAQS.map((item, i) => {
            const isOpen = openFaq === i;
            const panelId = `uiux-faq-panel-${i}`;
            const buttonId = `uiux-faq-button-${i}`;
            return (
              <div className="ea-faq-row" key={item.q}>
                <h3 style={{ margin: 0, fontSize: "inherit", fontWeight: "inherit" }}>
                  <button
                    id={buttonId}
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    onClick={() => setOpenFaq(isOpen ? -1 : i)}
                  >
                    {item.q}<span>{isOpen ? "−" : "+"}</span>
                  </button>
                </h3>
                <p id={panelId} role="region" aria-labelledby={buttonId} hidden={!isOpen}>{item.a}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* CONTACT CTA */}
      <section className="ea-cta" aria-labelledby="uiux-contact-heading">
        <div className="ea-wrap ea-cta-grid">
          <Reveal as="div" reduceMotion={reduceMotion}>
            <div className="ea-eyebrow">A practical first step</div>
            <h2 id="uiux-contact-heading">Ready to design something people love?</h2>
            <p>Tell us what you're building and we'll help you scope the right design engagement.</p>
          </Reveal>

          <Reveal as="div" i={1} reduceMotion={reduceMotion} className="ea-contact-card">
            <span>READY WHEN YOU ARE</span>
            <h3>Start your project.</h3>
            <p>Tell us what you're building and we'll get back to you within one business day.</p>

            <form onSubmit={handleContactSubmit} aria-label="UI/UX design project inquiry form" className="ea-form" noValidate>
              {contactStatus === "success" && (
                <p role="status" aria-live="polite" className="ea-form-msg success">
                  Thanks — we've received your details and will be in touch shortly.
                </p>
              )}
              {contactStatus === "error" && (
                <p role="alert" aria-live="assertive" className="ea-form-msg error">
                  Something went wrong submitting your request. Please try again.
                </p>
              )}

              <div className="ea-field">
                <label htmlFor="uiux-fullname">Full Name</label>
                <input
                  id="uiux-fullname"
                  name="fullname"
                  type="text"
                  value={formData.fullname}
                  onChange={handleContactChange}
                  aria-invalid={Boolean(contactErrors.fullname)}
                  aria-describedby={contactErrors.fullname ? "uiux-fullname-error" : undefined}
                />
                {contactErrors.fullname && <p id="uiux-fullname-error" className="ea-field-error">{contactErrors.fullname}</p>}
              </div>

              <div className="ea-form-row">
                <div className="ea-field">
                  <label htmlFor="uiux-phone">Phone Number (optional)</label>
                  <input
                    id="uiux-phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleContactChange}
                    aria-invalid={Boolean(contactErrors.phone)}
                    aria-describedby={contactErrors.phone ? "uiux-phone-error" : undefined}
                  />
                  {contactErrors.phone && <p id="uiux-phone-error" className="ea-field-error">{contactErrors.phone}</p>}
                </div>
                <div className="ea-field">
                  <label htmlFor="uiux-email">Email</label>
                  <input
                    id="uiux-email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleContactChange}
                    aria-invalid={Boolean(contactErrors.email)}
                    aria-describedby={contactErrors.email ? "uiux-email-error" : undefined}
                  />
                  {contactErrors.email && <p id="uiux-email-error" className="ea-field-error">{contactErrors.email}</p>}
                </div>
              </div>

              <div className="ea-field">
                <label htmlFor="uiux-subject">Subject</label>
                <input
                  id="uiux-subject"
                  name="subject"
                  type="text"
                  placeholder="e.g., A redesign of our product's core flows"
                  value={formData.subject}
                  onChange={handleContactChange}
                  aria-invalid={Boolean(contactErrors.subject)}
                  aria-describedby={contactErrors.subject ? "uiux-subject-error" : undefined}
                />
                {contactErrors.subject && <p id="uiux-subject-error" className="ea-field-error">{contactErrors.subject}</p>}
              </div>

              <div className="ea-field">
                <label htmlFor="uiux-description">Project Details</label>
                <textarea
                  id="uiux-description"
                  name="description"
                  rows={3}
                  value={formData.description}
                  onChange={handleContactChange}
                  aria-invalid={Boolean(contactErrors.description)}
                  aria-describedby={contactErrors.description ? "uiux-description-error" : undefined}
                />
                {contactErrors.description && <p id="uiux-description-error" className="ea-field-error">{contactErrors.description}</p>}
              </div>

              <button type="submit" disabled={contactStatus === "submitting"} className="ea-button ea-primary ea-submit">
                {contactStatus === "submitting" ? "Submitting…" : "Start a conversation ↗"}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default UIUXDesign;
